/* A BREATH WEAPON SHOULD GET WHAT A SPELL GETS.
 *
 * The effects engine is cleric-shaped: it draws shapes, asks for saves, applies
 * damage with resistances and runs the who-is-affected dialog -- for SPELLS, which
 * arrive as structured rows from D&D Beyond with an `aoe`, a `save` and a
 * `damage` array. A monster's breath weapon arrives as a sentence, so the DM runs
 * it by hand while the engine sits there knowing how.
 *
 * This reads the sentence. Nothing else: it produces the same description a spell
 * hands the engine, and the engine does what it already does.
 *
 * WHAT IT REFUSES TO GUESS is the important half. An action it cannot read
 * returns null and the DM runs it as they do today. A half-read action -- a cone
 * with no save, a save with no damage -- is worse than none, because the engine
 * would place a shape and then resolve nothing, which at a table reads as the
 * feature being broken. So every piece must be found or the whole is dropped.
 *
 * Stat block text is remarkably consistent across sources (SRD, D&D Beyond,
 * the Compass's own parser) because it is all derived from the same printed
 * grammar. The patterns below follow that grammar rather than any one source.
 */
import type { EffectDef, Shape } from "./effects";

export interface ActionEffect {
  /** the action's own name, as the stat block prints it */
  name: string;
  shape: Shape;
  /** radius for a sphere, length for a line or cone, side for a cube */
  sizeFt: number;
  /** a line's width */
  widthFt?: number;
  save?: "str" | "dex" | "con" | "int" | "wis" | "cha";
  dc?: number;
  /** the dice as written, "12d8" */
  dice?: string;
  dmgType?: string;
  /** "half as much damage on a successful one" */
  half: boolean;
  /** a condition the action imposes by name, lower case */
  condition?: string;
  /** "Recharge 5-6" or "Recharge 6", when the action has one */
  recharge?: string;
  /** every part this was read from, so a reader can check the parse */
  from: string;
}

const ABILITIES: Record<string, ActionEffect["save"]> = {
  strength: "str", dexterity: "dex", constitution: "con",
  intelligence: "int", wisdom: "wis", charisma: "cha",
};

const DAMAGE = "acid|bludgeoning|cold|fire|force|lightning|necrotic|piercing|poison|psychic|radiant|slashing|thunder";

/* The conditions a stat block actually imposes in a sentence. Kept as a list
   rather than a loose "the target is X" capture, which matched things like
   "the target is pushed 10 feet" and produced a condition called "pushed 10". */
const CONDITIONS = [
  "blinded", "charmed", "deafened", "frightened", "grappled", "incapacitated",
  "invisible", "paralyzed", "paralysed", "petrified", "poisoned", "prone",
  "restrained", "stunned", "unconscious", "exhaustion",
];

/* TWO GRAMMARS, NOT ONE.
 *
 * Measured against a real table on 2026-10-03, which is the only reason this is
 * here: every creature in that game is on Roll20's 2024 sheet, and the 2024
 * stat block is written differently enough that the 2014 patterns matched
 * nothing at all.
 *
 *   2014  "Each creature in that line must make a DC 18 Dexterity saving throw,
 *          taking 54 (12d8) acid damage on a failed save, or half as much
 *          damage on a successful one."
 *
 *   2024  "*Constitution Saving Throw:* DC 19, each creature in a 60-foot Cone.
 *          *Failure:* 54 (or [[12d8]] (12d8)) Cold damage. *Success:* Half damage."
 *
 * The ability comes BEFORE the DC, the outcomes are their own labelled
 * sentences, and the dice are wrapped in a Roll20 macro and then repeated in
 * plain text. So the text is normalised first -- emphasis and macros removed --
 * and each reader accepts both orders.
 */
const clean = (v: string) => String(v || "")
  /* Roll20 roll macros: "[[12d8]] (12d8)" is the same dice said twice, once for
     the sheet to roll and once for a human. Dropping the macro leaves the human
     copy, which is what a parser wants. */
  .replace(/\[\[[^\]]*\]\]/g, " ")
  /* The 2024 sheet writes its labels in markdown emphasis. */
  .replace(/\*+/g, " ")
  .replace(/\s+/g, " ")
  .trim();

/** "(Recharge 5-6)" / "(Recharge 6)" / "(Recharges after a Short or Long Rest)" */
export function rechargeOf(name: string): string | undefined {
  const m = /\((Recharge[^)]*)\)/i.exec(String(name || ""));
  return m ? clean(m[1]) : undefined;
}

/** The action's name without its recharge note. */
export const bareActionName = (name: string): string =>
  clean(String(name || "").replace(/\s*\(Recharge[^)]*\)\s*/i, " "));

/* THE SHAPE. The printed grammar is remarkably stable:
     "a 60-foot line that is 5 feet wide"
     "a 30-foot cone"
     "a 20-foot-radius sphere"   /  "a 20-foot radius"
     "a 15-foot cube"
     "a 30-foot-radius, 40-foot-high cylinder"
   A line's width is read separately because it follows the length in its own
   clause and is the one dimension a cone and a sphere never have. */
export function shapeOf(text: string): { shape: Shape; sizeFt: number; widthFt?: number; from: string } | null {
  const t = clean(text);
  const line = /(\d+)[- ]foot[- ]line/i.exec(t);
  if (line) {
    const w = /(\d+)\s*(?:feet|foot|ft\.?)\s*wide/i.exec(t);
    return { shape: "line", sizeFt: Number(line[1]), widthFt: w ? Number(w[1]) : 5, from: line[0] };
  }
  const cone = /(\d+)[- ]foot[- ]cone/i.exec(t);
  if (cone) return { shape: "cone", sizeFt: Number(cone[1]), from: cone[0] };
  const cube = /(\d+)[- ]foot[- ]cube/i.exec(t);
  if (cube) return { shape: "cube", sizeFt: Number(cube[1]), from: cube[0] };
  const cylinder = /(\d+)[- ]foot[- ]radius[^.]{0,40}cylinder/i.exec(t);
  if (cylinder) return { shape: "cylinder", sizeFt: Number(cylinder[1]), from: cylinder[0] };
  const sphere = /(\d+)[- ]foot[- ]radius(?:[- ](?:sphere|burst))?/i.exec(t);
  if (sphere) return { shape: "sphere", sizeFt: Number(sphere[1]), from: sphere[0] };
  /* "each creature within 20 feet of it" -- an emanation centred on the creature
     rather than a placed shape. Deliberately last: the phrases above are more
     specific and several of them contain "within". */
  const within = /within (\d+) (?:feet|foot|ft\.?)/i.exec(t);
  if (within) return { shape: "emanation", sizeFt: Number(within[1]), from: within[0] };
  return null;
}

/** "DC 18 Dexterity saving throw" */
export function saveOf(text: string): { save: ActionEffect["save"]; dc: number; from: string } | null {
  const t = clean(text);
  const ABIL = "strength|dexterity|constitution|intelligence|wisdom|charisma";
  /* 2014: "a DC 18 Dexterity saving throw" */
  const old = new RegExp("DC\\s*(\\d+)\\s+(" + ABIL + ")\\s+(?:saving throw|save)", "i").exec(t);
  if (old) return { save: ABILITIES[old[2].toLowerCase()], dc: Number(old[1]), from: old[0] };
  /* 2024: "Constitution Saving Throw: DC 19" -- ability first, DC after. */
  const now = new RegExp("(" + ABIL + ")\\s+saving throw:?\\s*DC\\s*(\\d+)", "i").exec(t);
  if (now) return { save: ABILITIES[now[1].toLowerCase()], dc: Number(now[2]), from: now[0] };
  return null;
}

/** "54 (12d8) acid damage" or "12d8 acid damage".
 *
 *  The DICE are taken, never the average. A stat block prints both and the
 *  average is what a DM uses when they are not rolling -- but this hands the
 *  engine something to roll, and rolling 12d8 is the point of asking it. */
export function damageOf(text: string): { dice: string; dmgType: string; from: string } | null {
  const t = clean(text);
  /* A SHORT GAP IS ALLOWED between the dice and the damage type, because the
     2024 form closes two brackets first: "54 (or 12d8 (12d8)) Cold damage".
     [^.] keeps it inside one sentence, so a second damage clause cannot be
     stitched onto the first one's dice. */
  const m = new RegExp("(\\d+d\\d+(?:\\s*[+-]\\s*\\d+)?)[^.]{0,24}?\\s(" + DAMAGE + ")\\s+damage", "i").exec(t);
  if (!m) return null;
  return { dice: m[1].replace(/\s+/g, ""), dmgType: m[2].toLowerCase(), from: m[0] };
}

/** "half as much damage on a successful one" */
export const halfOnSave = (text: string): boolean =>
  /half as much damage|half damage/i.test(clean(text));

/* The 2024 sheet writes the recharge with an EN DASH -- "Recharge 5-6" is
   printed "Recharge 5\u20136" -- which rechargeOf captures verbatim rather than
   normalising, so the DM sees what their sheet says. */

/** A condition the action imposes, by name. */
export function conditionOf(text: string): string | undefined {
  const t = clean(text).toLowerCase();
  /* Only when the sentence says the target BECOMES it. "immune to the poisoned
     condition" and "can't be frightened" are the opposite claim, and reading
     them as an application is how a resistance becomes an affliction. */
  for (const c of CONDITIONS) {
    /* "or BE stunned until the end of its next turn" is how a stat block most
       often says it -- the sentence is a consequence of a failed save, not a
       statement about the creature. Leaving "be" out read every such action as
       imposing nothing. */
    const re = new RegExp("(?:is|are|be|becomes|has the)\\s+(?:\\w+\\s+){0,2}" + c, "i");
    if (!re.test(t)) continue;
    if (new RegExp("(?:immune|immunity|can'?t be|cannot be|isn'?t)\\s+(?:\\w+\\s+){0,3}" + c, "i").test(t)) continue;
    return c === "paralysed" ? "paralyzed" : c;
  }
  return undefined;
}

/** Read an action into something the effects engine can run, or null.
 *
 *  NULL IS THE COMMON ANSWER and that is correct: most actions are a melee
 *  attack against one target, which the card feed already handles. This is for
 *  the ones with an AREA, which are the ones a DM runs by hand today. */
export function actionEffect(name: string, text: string): ActionEffect | null {
  const t = clean(text);
  if (!t) return null;

  const shape = shapeOf(t);
  if (!shape) return null;

  const save = saveOf(t);
  const damage = damageOf(t);
  const condition = conditionOf(t);

  /* AN AREA THAT RESOLVES NOTHING IS NOT WORTH PLACING. The engine would draw
     the shape, ask who is inside, and then have nothing to apply -- which reads
     as the feature being broken rather than as the action being unusual. */
  if (!damage && !condition) return null;
  /* Damage with no save is a thing (an aura that simply burns), but damage with
     a save and no DC is a half-read sentence. saveOf only returns with a DC, so
     this is really a guard against a future looser saveOf. */
  if (save && !save.dc) return null;

  return {
    name: bareActionName(name),
    shape: shape.shape,
    sizeFt: shape.sizeFt,
    widthFt: shape.widthFt,
    save: save?.save,
    dc: save?.dc,
    dice: damage?.dice,
    dmgType: damage?.dmgType,
    half: halfOnSave(t),
    condition,
    recharge: rechargeOf(name),
    from: [shape.from, save?.from, damage?.from].filter(Boolean).join(" · "),
  };
}

/** The same description a spell hands the engine.
 *
 *  The engine takes an EffectDef and does the rest -- draws the shape, asks who
 *  is inside, rolls, applies with resistances. Nothing here teaches it anything
 *  new; it just says a breath weapon in the language it already speaks.
 *
 *  INSTANT, ALWAYS. A breath weapon goes off and is done: it is not a zone that
 *  lingers and catches people walking through. Getting this wrong would leave a
 *  dragon's breath sitting on the map proccing for the rest of the fight, which
 *  is the single most costly mistake available here -- so the trigger is `cast`
 *  and nothing else.
 *
 *  A standing area a monster maintains does exist (a lich's aura), but a stat
 *  block says so in words this parser does not read, and guessing which is which
 *  from a sentence is exactly the half-reading the parser refuses to do.
 */
export function toEffectDef(a: ActionEffect): EffectDef {
  return {
    key: `monster-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`,
    name: a.name,
    match: new RegExp("^" + a.name.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\/** Every action in a stat block that the engine could run. */") + "$", "i"),
    shape: a.shape,
    sizeFt: a.sizeFt,
    widthFt: a.widthFt,
    /* Placed where the DM puts it, not carried by the creature. */
    mobility: "immobile",
    application: "static",
    triggers: ["cast"],
    instant: true,
    rounds: null,
    concentration: false,
    save: a.save,
    half: a.half,
    dice: a.dice,
    dmgType: a.dmgType,
    condition: a.condition,
    /* A MONSTER'S BREATH SPARES NOBODY. A spell cast by a party member defaults
       to sparing allies; a dragon breathing on a crowd catches its own kin, and
       the DM would rather untick one than discover a goblin was quietly immune. */
    spares: "none",
    /* There is no player behind a dragon. Without this the engine waits for a
       Beyond20 roll that is never coming and every prompt reads zero. */
    noPlayerToRoll: true,
    color: COLORS[a.dmgType || ""] || "c75b39",
    keyword: a.name,
  };
}

/* The engine's own palette, by damage type, so a monster's area looks like the
   spell equivalent rather than arriving in a colour nothing else uses. */
const COLORS: Record<string, string> = {
  acid: "7ac74f", cold: "7fd4e8", fire: "e2683c", force: "b58ae0", lightning: "f2c744",
  necrotic: "5a5a6e", poison: "6aa84f", psychic: "d87fd8", radiant: "f5e6a8", thunder: "9fb6d4",
};

/** Every action in a stat block that the engine could run. */
export function runnableActions(sections: { key: string; entries: { name: string; text: string }[] }[]): ActionEffect[] {
  const out: ActionEffect[] = [];
  for (const s of sections) {
    for (const e of s.entries) {
      const hit = actionEffect(e.name, e.text);
      if (hit) out.push(hit);
    }
  }
  return out;
}
